import { type JSX, type ReactNode, useEffect, useState } from 'react';
import {
  isResolving,
  type QueryLike,
  type QueryPhaseOptions,
  type QueryState,
  queryPhase,
  RESOLVE_PATIENCE_MS,
} from './query-state';

/**
 * The React half of `query-state`: the patience timer, and the component that
 * makes the error arm impossible to forget.
 *
 * See `query-state.ts` for why a screen must never branch on `isPending`.
 */

/**
 * `true` once `active` has stayed true for longer than `RESOLVE_PATIENCE_MS`.
 *
 * The backstop behind every spinner. Nothing is cancelled when it fires, so a
 * slow request that lands afterwards still resolves normally; the timer only
 * decides what the reader is shown in the meantime.
 */
export function useStalled(active: boolean, afterMs: number = RESOLVE_PATIENCE_MS): boolean {
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!active) {
      setStalled(false);
      return;
    }
    const timer = setTimeout(() => setStalled(true), afterMs);
    return () => clearTimeout(timer);
  }, [active, afterMs]);

  return stalled;
}

/** `queryPhase` with the deadline wired in. This is what screens call. */
export function useQueryState<T>(
  query: QueryLike<T>,
  options: Omit<QueryPhaseOptions<T>, 'stalled'> = {},
): QueryState<T> {
  const stalled = useStalled(isResolving(query) && options.disabled !== true);
  return queryPhase(query, { ...options, stalled });
}

export interface QueryBoundaryProps<T> {
  query: QueryLike<T>;
  /**
   * The failure arm. REQUIRED - that is the whole point of this component. Every
   * app passes its own error card here, because the sentence, the request id and
   * the way back (sign in again, rescan the code) are app-specific; what is not
   * app-specific is that one must exist.
   */
  error: (error: unknown) => ReactNode;
  /** The success arm. */
  children: (data: T) => ReactNode;
  /** Shown while the query is honestly still resolving. */
  pending?: ReactNode;
  /** Shown when the response is well-formed and has nothing in it. */
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  /** See `QueryPhaseOptions.disabled`. */
  disabled?: boolean;
}

/**
 * Renders exactly one of pending, error, empty or ready.
 *
 * A screen composed of these cannot show stale content beside an error, cannot
 * fall through to a blank page, and - because `pending` is bounded by the
 * deadline in `useStalled` - cannot leave a reader on a spinner that will never
 * resolve.
 */
export function QueryBoundary<T>({
  query,
  error,
  children,
  pending = null,
  empty = null,
  isEmpty,
  disabled,
}: QueryBoundaryProps<T>): JSX.Element {
  const state = useQueryState(query, {
    ...(isEmpty === undefined ? {} : { isEmpty }),
    ...(disabled === undefined ? {} : { disabled }),
  });

  if (state.phase === 'error') return <>{error(state.error)}</>;
  if (state.phase === 'pending') return <>{pending}</>;
  if (state.phase === 'empty') return <>{empty}</>;
  return <>{children(state.data)}</>;
}
